import {Component, inject, OnInit, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {CommonDropdownComponent} from '@lib-ui/common-dropdawn/common-dropdown.component';
import {FormBuilder, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatButton} from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MessageService} from '@lib-services/message.service';
import {PriceService} from '@lib-services/price.service';
import {IPrice, IPriceCreate, IPriceUpdate} from '@lib-typing/price.interface';
import {Price} from '@libs-classes/price.class';
import {CategorySelectorService} from '@lib-services/category-selector.service';
import {WorkSelectorService} from '@lib-services/work-selector.sevice';
import {LoaderComponent} from '@lib-ui/loader/loader.component';
import {filter, switchMap} from 'rxjs';
import { CloseButtonComponent } from '@lib-ui/close-button/close-button.component';
import {regex} from '@libs-constants/regex';
import { ExternalPriceSelectorService } from '@lib-services/external-price-selector.service';
import { AuthService } from '@lib-ui/auth/auth.service';

@Component({
    selector: 'app-price-editor',
    standalone: true,
    imports: [
        CommonModule,
        CommonDropdownComponent,
        FormsModule,
        MatButton,
        MatDialogContent,
        MatFormField,
        MatInput,
        MatLabel,
        ReactiveFormsModule,
        LoaderComponent,
        CloseButtonComponent,
        MatError,
        MatDialogTitle,
    ],
    templateUrl: './price-editor.component.html',
    styleUrl: './price-editor.component.scss'
})
export class PriceEditorComponent implements OnInit {
    price = new Price({} as IPrice);
    loading = signal(false);

    readonly dialogRef = inject(MatDialogRef<PriceEditorComponent>);
    readonly fb = inject(FormBuilder);
    readonly categorySelectorService = inject(CategorySelectorService);
    protected priceService = inject(PriceService);
    protected workSelectorService = inject(WorkSelectorService);
    protected externalPriceSelectorService = inject(ExternalPriceSelectorService);
    protected messagesService = inject(MessageService);
    protected authService = inject(AuthService);
    public data: { priceId?: number } = inject(MAT_DIALOG_DATA);
    private regex = regex;

    priceForm= this.fb.group({
        categoryId: [0, [Validators.required, Validators.pattern(this.regex.naturalNumber)]],
        price: [0, Validators.required],
        discountPrice: [0],
        workId: [0, [Validators.required, Validators.pattern(this.regex.naturalNumber)]],
        duration: [0, Validators.required],
        externalPriceId: [null as number | null],
    });

    ngOnInit(): void {
        if (this.data.priceId) {
            this.loading.set(true);
            this.priceService.getPrice(this.data.priceId).subscribe( {
                next: data => {
                    this.price.data = data;
                    this.priceForm.setValue(this.price.formData());
                },
                error: error => {this.messagesService.errorMessage(error.message);}
            }).add(() => this.loading.set(false));
        }
    }

    onSubmit(): void {
        if (!this.priceForm.valid) {
            return;
        }

        this.loading.set(true);
        const request = this.price.data.id
            ? this.priceService.updatePrice(this.price.dataForUpdate(this.priceForm.value as IPriceUpdate))
            :  this.priceService.createPrice(this.price.dataForCreate(this.priceForm.value as IPriceCreate));

        request.subscribe({
            next: result => {
                this.messagesService.successMessage('Прайс успешно сохранен');
                this.dialogRef.close(result);},
            error: error => {this.messagesService.errorMessage(error.message);}
        }).add(() => this.loading.set(false));
    }

    deletePrice(id: number): void {
        if(!this.price.data) {return;}

        this.messagesService.confirmMessage({
            title: 'Удаление прайса',
            text: 'Вы действительно хотите удалить прайс?',
            primaryText: 'Удалить',
            secondaryText: 'Отмена',
            showSecondaryButton: true,
        }).pipe(
            filter(result => result),
            switchMap(() => {
                return this.priceService.deletePrices([id]);
            }),
        ).subscribe({
            next: () => {
                this.messagesService.successMessage('Прайс успешно удалены');
                this.dialogRef.close(true);
            },
            error: () => {
                this.messagesService.errorMessage('При удалении произошла ошибка');
            }
        });
    }

    get canUsePaymentDevice(): boolean {
        return !!this.authService.user().data?.company?.settings?.paymentDevice;
    }
}
